import { useCallback, useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { parseISO } from 'date-fns';
import { useDebounce } from '@uidotdev/usehooks';
import { DarwinRow, DarwinDivider, DarwinSpin } from 'components/darwin-ui';
import { useUI } from 'contexts/UiContext';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useGetAssignableRolesForCompany } from 'api/services/rolesService';
import { useGetAssignableOrganizationsForCompany } from 'api/services/organizationService';
import {
  pregenerateUserWallet,
  useCreateNewUser,
  useEditUser,
  useGetUserById,
  useUploadAvatarFile,
  useValidateEmailAndOrganizationRelation,
} from 'api/services/userService';
import { extractQueryData } from 'utils/services';
import { AvatarUploadInput } from '../../components/ui/users/newUserForm';
import {
  ValidateUserSection,
  AdditionalSettingSection,
  PersonalInformationSection,
  RoleDetailSection,
} from '../../components/ui/users/newUserForm/sections';
import styles from '../../components/ui/users/Users.module.css';
import { MetadataSection } from 'components/ui/users/newUserForm/sections/MetadataSection';
import {
  CreateNewUserPayload,
  CreateUserFormData,
  EditUserPayload,
} from 'types/userTypes';
import { generateRandomAddress } from 'components/ui/users/newUserForm/utils';
import { ROUTES } from 'routes/routes';
import { RoleType } from 'types/rolesTypes';

type CreateUserFormProps = {
  formAction?: 'create' | 'edit' | 'view';
};

const baseDefaultValues = {
  avatar: [],
  email: '',
  organization: '',
  firstName: '',
  lastName: '',
  countryCode: '',
  contactPhone: { phoneCode: '', phoneNumber: '' },
  role: '',
  jobTitle: '',
  language: '',
  timeZone: '',
};

const DEBOUNCE_DELAY = 3000;

export const CreateUserForm = ({
  formAction = 'create',
}: CreateUserFormProps) => {
  const isCreateForm = formAction === 'create';
  const isEditForm = formAction === 'edit';
  const isViewForm = formAction === 'view';
  const defaultValuesAdditionalProperties = !isCreateForm
    ? {
        createdBy: '',
        createdAt: '',
        updatedBy: '',
        updatedAt: '',
        active: true,
      }
    : {};

  // hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const [initialValuesOnViewForm, setInitialValuesOnViewForm] =
    useState<CreateUserFormData>({
      avatar: [],
      email: '',
      organization: '',
      firstName: '',
      lastName: '',
      countryCode: '',
      contactPhone: { phoneCode: '', phoneNumber: '' },
      role: '',
      jobTitle: '',
      language: '',
      timeZone: '',
      active: true,
    });

  const [isWeb3FeatureLoading, setIsWeb3FeatureLoading] =
    useState<boolean>(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
    setError,
    setValue,
  } = useForm<CreateUserFormData>({
    defaultValues: {
      ...baseDefaultValues,
      ...defaultValuesAdditionalProperties,
    },
    mode: 'onChange',
  });

  const { mutateAsync: createUser, isPending: isCreateUserPending } =
    useCreateNewUser();

  const { mutateAsync: editUser, isPending: isEditUserPending } = useEditUser();

  const {
    mutateAsync: uploadUserAvatar,
    isPending: isUploadingUserAvatarPending,
  } = useUploadAvatarFile();

  // user system
  const systemLanguage = (navigator.language || 'es-ES').split('-')[0];
  const offset = new Date().getTimezoneOffset();
  const systemTimeZone = `GMT${offset <= 0 ? '+' : '-'}${
    Math.abs(offset) / 60
  }`;

  // get initial data when formAction = create
  const { selectedCompany } = useUI();
  const assignableRolesQuery = useGetAssignableRolesForCompany(
    selectedCompany || ''
  );
  const assignableRoles = extractQueryData(assignableRolesQuery, undefined);

  const assignableOrganizationsQuery = useGetAssignableOrganizationsForCompany(
    selectedCompany || ''
  );
  const assignableOrganizations = extractQueryData(
    assignableOrganizationsQuery,
    undefined
  );

  // get initial data when formAction = view | edit
  const { id: userCompanyRoleId } = useParams();
  const userByIdQuery = useGetUserById(
    selectedCompany || '',
    userCompanyRoleId || ''
  );
  const userByIdData = extractQueryData(userByIdQuery, undefined);

  const isOwner = userByIdData?.role.name === RoleType.OWNER;

  useEffect(() => {
    if (
      !isCreateForm &&
      userByIdData &&
      selectedCompany &&
      assignableOrganizations &&
      assignableRoles
    ) {
      const { user, role, company } = userByIdData;
      const {
        signedUrl,
        email,
        firstName,
        lastName,
        countryCode,
        phoneNumber,
        phoneCode,
        language,
        timeZone,
        createdBy,
        createdAt,
        updatedBy,
        updatedAt,
      } = user;

      const viewPayload = {
        avatar: signedUrl,
        email,
        organization:
          assignableOrganizations.find(
            organization => organization.id === company.id
          )?.id || '',
        firstName,
        lastName,
        countryCode,
        contactPhone: { phoneCode: phoneCode.slice(1), phoneNumber },
        role: isOwner
          ? RoleType.OWNER
          : assignableRoles.find(
              assignableRole => assignableRole.id === role.id
            )?.id || '',
        jobTitle: userByIdData.jobTitle,
        language,
        timeZone,
        active: userByIdData.active,
        createdBy: createdBy
          ? `${createdBy?.firstName} ${createdBy?.lastName}`
          : '',
        createdAt: parseISO(createdAt),
        updatedBy: updatedBy
          ? `${updatedBy?.firstName} ${updatedBy?.lastName}`
          : '',
        updatedAt: parseISO(updatedAt),
      };
      reset(viewPayload);
      setInitialValuesOnViewForm(viewPayload);
    }
  }, [
    assignableOrganizations,
    assignableRoles,
    isCreateForm,
    reset,
    selectedCompany,
    userByIdData,
    isOwner,
  ]);

  // email+organization validation when formAction = create
  const [
    canCreateEmailOrganizationRelation,
    setCanCreateEmailOrganizationRelation,
  ] = useState(false);
  const selectedCountryCode = watch('countryCode');
  const emailValue = watch('email');
  const organizationValue = watch('organization');
  const userStatusValue = watch('active');
  const avatarImageUrl = watch('avatar');
  const { refetch, isFetching } = useValidateEmailAndOrganizationRelation(
    emailValue,
    organizationValue
  );
  const debouncedEmail = useDebounce(emailValue, DEBOUNCE_DELAY);
  const debouncedOrganization = useDebounce(organizationValue, DEBOUNCE_DELAY);

  // debounce, validation logic and set form values after validation
  useEffect(() => {
    if (debouncedEmail && debouncedOrganization && isCreateForm) {
      refetch().then(result => {
        if (result.data) {
          const canCreateRelation = result.data.data;

          if (typeof canCreateRelation === 'object') {
            const {
              firstName,
              lastName,
              countryCode,
              phoneNumber,
              phoneCode,
              jobTitle,
              language,
              timeZone,
            } = canCreateRelation;

            reset({
              email: debouncedEmail,
              organization: debouncedOrganization,
              firstName,
              lastName,
              countryCode,
              contactPhone: {
                phoneCode,
                phoneNumber,
              },
              jobTitle,
              language,
              timeZone,
            });
          } else if (canCreateRelation === true) {
            reset({
              email: debouncedEmail,
              organization: debouncedOrganization,
              language: systemLanguage,
              timeZone: systemTimeZone,
            });
          } else {
            setError('email', {
              type: 'manual',
              message: t(
                'create-user-form.email-organization-validation-error'
              ),
            });
          }

          setCanCreateEmailOrganizationRelation(canCreateRelation);
        }
      });
    }
  }, [
    refetch,
    reset,
    systemLanguage,
    systemTimeZone,
    setError,
    t,
    debouncedEmail,
    debouncedOrganization,
    isCreateForm,
  ]);

  const buildInitialValues = (data: CreateNewUserPayload | EditUserPayload) => {
    return {
      avatar: data.imageUrl || null,
      email: data.email || '',
      organization: data.companyId || '',
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      countryCode: data.countryCode || '',
      contactPhone: {
        phoneCode: data.phoneCode?.slice(1) || '',
        phoneNumber: data.phoneNumber || '',
      },
      role: data.roleId || '',
      jobTitle: data.jobTitle || '',
      language: data.language || '',
      timeZone: data.timeZone || '',
      createdBy: undefined,
      createdAt: undefined,
      updatedBy: undefined,
      updatedAt: undefined,
      active: true,
    };
  };

  const currentValues = useWatch({ control });

  const isEditFormModified =
    JSON.stringify(currentValues) !== JSON.stringify(initialValuesOnViewForm);

  // submit form logic
  const handleUploadUserAvatar = async (file: File) => {
    try {
      const uploadAvatarResponse = await uploadUserAvatar({
        file,
      });
      return uploadAvatarResponse.data.id;
    } catch (error) {
      console.log(error);
    }
  };

  const handleGenerateWeb3Data = async (email: string) => {
    try {
      setIsWeb3FeatureLoading(true);
      const pregeneratedWalletId = await pregenerateUserWallet(email);
      return { pregeneratedWalletId };
    } catch (error) {
      console.log(error);
    } finally {
      setIsWeb3FeatureLoading(false);
    }
  };

  const handleCreateUser = (
    payload: CreateNewUserPayload,
    saveAndExit?: boolean
  ) => {
    createUser({
      userData: payload,
      companyId: selectedCompany || '',
    })
      .then(response => {
        openNotification(
          t('success'),
          t('create-user-form.create-new-user-success'),
          'success'
        );
        if (saveAndExit) navigate(ROUTES.USERS.LIST);
        if (!saveAndExit) {
          navigate(
            generatePath(ROUTES.USERS.EDIT, {
              id: response.data.id,
            })
          );
        }
      })
      .catch(() =>
        openNotification(
          t('error'),
          t('create-user-form.create-new-user-error'),
          'error'
        )
      );
  };

  // Auto Complete Prefix
  const onSelectCountry = useCallback(
    (phoneCode: string) => {
      //Validate if we have phoneCode and phoneNumber
      const phone = watch('contactPhone');
      const contactPhone = { phoneCode: phoneCode, phoneNumber: '' };
      if (phone) {
        setValue('contactPhone', {
          phoneCode: '',
          phoneNumber: phone.phoneNumber,
        });
        contactPhone.phoneNumber = phone.phoneNumber;
      }
      setValue('contactPhone', contactPhone);
    },
    [setValue, watch]
  );

  const handleEditUser = (payload: EditUserPayload, saveAndExit?: boolean) => {
    editUser({
      userData: payload,
      companyId: selectedCompany || '',
      userCompanyRoleId: userCompanyRoleId || '',
    })
      .then(() => {
        openNotification(
          t('success'),
          t('edit-user-form.edit-user-success'),
          'success'
        );
        if (saveAndExit) navigate(ROUTES.USERS.LIST);
        const data = buildInitialValues(payload);
        if (!saveAndExit) setInitialValuesOnViewForm(data);
      })
      .catch(() =>
        openNotification(
          t('error'),
          t('edit-user-form.edit-user-error'),
          'error'
        )
      );
  };

  const onSubmit =
    (saveAndExit: boolean): SubmitHandler<CreateUserFormData> =>
    async data => {
      // upload avatar
      let imageFileId: string | undefined;
      if (
        data.avatar &&
        data.avatar.length > 0 &&
        typeof data.avatar !== 'string'
      ) {
        imageFileId = await handleUploadUserAvatar(data.avatar[0]);
      }

      const { avatar, organization, role, contactPhone, ...rest } = data;

      let roleId = role;

      if (isOwner && isEditForm) {
        roleId = userByIdData.role.id;
      }

      if (isCreateForm) {
        const generatedWeb3Data = await handleGenerateWeb3Data(data.email);

        const createPayload = {
          ...rest,
          imageFileId,
          roleId,
          companyId: organization,
          phoneNumber: contactPhone.phoneNumber,
          phoneCode: `+${contactPhone.phoneCode}`,
          txHash: generateRandomAddress(),
          walletId: generatedWeb3Data?.pregeneratedWalletId,
        };

        await handleCreateUser(createPayload, saveAndExit);
      } else {
        const editPayload = {
          ...rest,
          imageFileId,
          roleId,
          phoneNumber: contactPhone.phoneNumber,
          phoneCode: `+${contactPhone.phoneCode}`,
          companyId: organization,
        };

        await handleEditUser(editPayload, saveAndExit);
      }
    };

  // header button logic
  useEffect(() => {
    if (isEditForm) {
      setCanCreateEmailOrganizationRelation(true);
    }
  }, [isEditForm]);

  const isSaveButtonDisabled =
    isCreateUserPending ||
    emailValue === '' ||
    organizationValue === '' ||
    !canCreateEmailOrganizationRelation ||
    isWeb3FeatureLoading ||
    isUploadingUserAvatarPending ||
    !isEditFormModified ||
    isEditUserPending ||
    !isValid;

  const isLoadingInitialDataOrSubmittingForm =
    assignableOrganizationsQuery.isLoading ||
    assignableRolesQuery.isLoading ||
    userByIdQuery.isLoading ||
    isCreateUserPending ||
    isEditUserPending ||
    isWeb3FeatureLoading;

  return (
    <>
      <ContentHeader
        title={
          isCreateForm
            ? t('new-user')
            : isEditForm
              ? t('edit-user-form.title')
              : t('view-user-form.title')
        }
        onSave={handleSubmit(onSubmit(false))}
        onCancel={() => navigate(ROUTES.USERS.LIST)}
        saveDisabled={isSaveButtonDisabled}
        extraButtonDisabled={isSaveButtonDisabled}
        displayExtraButton={true}
        extraButtonTitle={t('save-exit')}
        onExtraButtonClick={handleSubmit(onSubmit(true))}
      />

      <DarwinSpin fullscreen spinning={isLoadingInitialDataOrSubmittingForm} />

      <Form
        className={styles.userFormContainer}
        layout="vertical"
        onFinish={handleSubmit(onSubmit(false))}
      >
        {/* Avatar - This section is being commented on by Demo realizations */}

        {/* <DarwinRow align={'middle'}>
          <AvatarUploadInput
            control={control}
            errors={errors}
            canCreateEmailOrganizationRelation={
              canCreateEmailOrganizationRelation
            }
            isCreateOrEditForm={isCreateForm || isEditForm}
            imageUrl={avatarImageUrl}
          />
        </DarwinRow>

        <DarwinDivider /> */}

        {/* Validate User Section */}
        <ValidateUserSection
          control={control}
          errors={errors}
          assignableOrganizations={assignableOrganizations || []}
          isFetching={isFetching}
          isViewForm={isViewForm}
        />
        {/* Personal Information Section */}
        <PersonalInformationSection
          control={control}
          errors={errors}
          canCreateEmailOrganizationRelation={
            canCreateEmailOrganizationRelation || isEditForm
          }
          selectedCountryCode={selectedCountryCode}
          onSelectCountry={onSelectCountry}
          isCreateForm={isCreateForm}
        />

        {/* Role Detail Section */}
        <RoleDetailSection
          control={control}
          errors={errors}
          canCreateEmailOrganizationRelation={
            canCreateEmailOrganizationRelation || isEditForm
          }
          assignableRoles={assignableRoles || []}
          isOwner={isOwner}
        />

        {/* Additional Settings Section */}
        <AdditionalSettingSection
          control={control}
          errors={errors}
          canCreateEmailOrganizationRelation={
            canCreateEmailOrganizationRelation || isEditForm
          }
          isCreateForm={isCreateForm}
          active={userStatusValue}
        />

        {/* Metadata Section */}
        {!isCreateForm && (
          <MetadataSection
            control={control}
            errors={errors}
            isEditForm={isEditForm}
          />
        )}
      </Form>
    </>
  );
};
